import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  Input,
  CircularProgress,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Select,
  Tooltip,
} from "@chakra-ui/react";

import { getFPConfig, postFPConfig } from "../utils/api";
import {
  GETFPConfigResponseBody,
  FPConfigFloatFields,
  FPConfigIntFields,
  FPConfigSelectFields,
  GrupoConfig,
} from "../utils/interfaces";
import { AxiosError } from "axios";

interface FPConfigFormProps {
  nfp: number;
}

export default function FPConfigForm({ nfp }: FPConfigFormProps): JSX.Element {
  const toast = useToast();
  const [maybeResponse, setResponse] = useState<GETFPConfigResponseBody | null>(null);

  useEffect(() => {
    getFPConfig(nfp)
      .then(res => setResponse(res.data))
      .catch(err => console.log("Error al obtener configuración:", err));
  }, [nfp]);

  if (maybeResponse === null) {
    return (
      <VStack justifyContent="center" my="12" w="100%">
        <CircularProgress isIndeterminate size={120} color="teal" />
        <Text fontSize="2xl">Cargando configuración...</Text>
      </VStack>
    );
  }

  const { fpConfig, generalConfig } = maybeResponse;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    /**
     * POST /semaphore/config llama a la función POSTConfig, definida en
     * semaphoreback/api/post_config.go
     */
    postFPConfig(fpConfig)
      .then((_) => {
        setResponse({ fpConfig: { ...fpConfig, hay_cambios_sin_leer: 1}, generalConfig: generalConfig })
      })
      .catch((err: AxiosError) => {
        // Se muestra un toast con el error plasmado en errorMessage.
        let errorMessage = "";
        if (err.response) {
          // Si el backend respondió con un error (código no 2xx):
          errorMessage = err.response.data as string;
        } else if (err.request) {
          // Si hubo petición, pero no respuesta del servidor:
          errorMessage = "Error de conexión con el servidor."
        } else {
          // Si algo más salió mal:
          errorMessage = err.message;
        }
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 9000,
          isClosable: true,
        })
      });
  };

  // Función necesaria para desactivar el scrolling en los campos numéricos
  // Por defecto el scrolling cambia el valor en esos campos, lo cual es molesto
  // Esta función desactiva eso
  const disableScroll = (event: React.WheelEvent<HTMLInputElement>) => {
    (event.target as HTMLElement).blur();
  };

  /*
    Nota para todas las funciones onChange:
    event.target se refiere al input en sí (Input, TimeInput, Radio)
    event.target.name es el parámetro name pasado al input
    event.target.value es el valor actual almacenado por el input
    En el caso de Select, se debe usar event.target.selectedOptions para acceder
    a las opciones disponibles a seleccionar
  */

  const onChangeSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    fpConfig[event.target.name as keyof FPConfigSelectFields] = parseInt(event.target.selectedOptions[0].value);
  };

  const onChangeInt = (event: React.ChangeEvent<HTMLInputElement>) => {
    fpConfig[event.target.name as keyof FPConfigIntFields] = parseInt(event.target.value);
  };

  const onChangeFloat = (event: React.ChangeEvent<HTMLInputElement>) => {
    fpConfig[event.target.name as keyof FPConfigFloatFields] = parseFloat(event.target.value);
  };

  //   const onChangeBoolean = (event: React.ChangeEvent<HTMLInputElement>) => {
  //     localConfig[event.target.name as keyof SemaphoreConfigBooleanFields] = event.target.value === "true";
  //   }

  //   const onChangeString = (event: React.ChangeEvent<HTMLInputElement>) => {
  //     localConfig[event.target.name as keyof SemaphoreConfigStringFields] = event.target.value;
  //   };

  /* Funciones auxiliares para evitar repetir tanto código */

  const MyHeading = ({ heading }: { heading: string }): JSX.Element => {
    return (
      <Heading as="h3" size="md" py={4}>
        {heading}
      </Heading>
    );
  };

  const MyLabel = ({ label }: { label: string }): JSX.Element => {
    return (
      <Box w="40%">
        <Text>
          <b>{label}</b>
        </Text>
      </Box>
    );
  };

  const MySelect = ({ label, name, options }: { label: string, name: string, options: GrupoConfig[] }): JSX.Element => {
    const defaultID = fpConfig[name as keyof FPConfigSelectFields];
    return (
      <HStack>
        <MyLabel label={label} />
        <Select
          name={name}
          width="60%"
          onChange={onChangeSelect}
          defaultValue={defaultID}
        >
          {options.map(op => (
            <option key={op.id} value={op.id}>
              {op.id}. {op.nombre}
            </option>
          ))}
        </Select>
      </HStack>
    );
  }

  const IntInput = ({ label, name, tooltip }: { label: string; name: keyof FPConfigIntFields, tooltip?: string }): JSX.Element => {
    return (
      <HStack>
        <Tooltip label={tooltip}>
          <MyLabel label={label} />
        </Tooltip>
        <Input
          name={name}
          defaultValue={fpConfig[name]}
          placeholder="0"
          onChange={onChangeInt}
          onWheel={disableScroll}
          type="number"
          step="1"
          variant="flushed"
          htmlSize={40}
          width="60%"
        />
      </HStack>
    );
  };

  const FloatInput = ({ label, name }: { label: string; name: keyof FPConfigFloatFields }): JSX.Element => {
    return (
      <HStack>
        <MyLabel label={label} />
        <Input
          name={name}
          defaultValue={fpConfig[name]}
          placeholder="0.000"
          onChange={onChangeFloat}
          onWheel={disableScroll}
          type="number"
          step="any"
          variant="flushed"
          htmlSize={40}
          width="60%"
        />
      </HStack>
    );
  };

  const MinMaxHead = (): JSX.Element => {
    return (
      <HStack>
        <Box width="40%" />
        <Text width="30%" textAlign="center" fontWeight="bold">Mínimo</Text>
        <Text width="30%" textAlign="center" fontWeight="bold">Máximo</Text>
      </HStack>
    );
  };

  const MinMaxInput = ({
    label,
    nameTemplate,
  }: {
    label: string;
    nameTemplate: string;
  }): JSX.Element => {
    const minName = nameTemplate.replace("%s", "min") as keyof FPConfigFloatFields;
    const maxName = nameTemplate.replace("%s", "max") as keyof FPConfigFloatFields;
    return (
      <HStack>
        <MyLabel label={label} />
        <Input
          name={minName}
          defaultValue={fpConfig[minName]}
          placeholder="0"
          disabled={fpConfig[minName] === undefined}
          onChange={fpConfig[minName] === undefined ? () => {} : onChangeFloat}
          onWheel={disableScroll}
          type="number"
          step="any"
          variant="flushed"
          htmlSize={20}
          width="30%"
        />
        <Input
          name={maxName}
          defaultValue={fpConfig[maxName]}
          placeholder="0.000"
          disabled={fpConfig[maxName] === undefined}
          onChange={fpConfig[maxName] === undefined ? () => {} : onChangeFloat}
          onWheel={disableScroll}
          type="number"
          step="any"
          variant="flushed"
          htmlSize={40}
          width="30%"
        />
      </HStack>
    );
  };

  // const TimeInput = ({ label }: { label: string }) => {
  //     const UnitField = ({ unit }: { unit: "days" | "hours" | "minutes" | "seconds" }) => {
  //         // Asumiendo que namePrefix === "yellowTime":
  //         // Ejemplo con unit === "hours": se asigna initial === "h",
  //         // name === "yellowTimeHours", placeholder === "hh" y max === 23
  //         const initial = unit.charAt(0);
  //         const name = `measurementInterval${initial.toUpperCase()}${unit.slice(1)}`;
  //         const placeholder = initial + initial;
  //         const max = (unit === "days") ? 99 : ((unit === "hours") ? 23 : 59);
  //         return (
  //             <HStack spacing={0}>
  //                 <Input
  //                     name={name} defaultValue={config[name as keyof SemaphoreConfigIntFields]} placeholder={placeholder}
  //                     min={0} max={max}
  //                     onChange={onChangeInt} onWheel={disableScroll}
  //                     type="number" variant="flushed" maxW={5}
  //                 />
  //                 <Text>{initial}</Text>
  //             </HStack>
  //         );
  //     }

  //     return (
  //         <HStack>
  //             <MyLabel label={label} />
  //             <HStack spacing={6}>
  //                 <UnitField unit="days" />
  //                 <UnitField unit="hours" />
  //                 <UnitField unit="minutes" />
  //                 <UnitField unit="seconds" />
  //             </HStack>
  //         </HStack>
  //     );
  // }

  return (
    <form onSubmit={onSubmit} method="post">
      <VStack align="left" spacing={1}>
        <MySelect label="Grupo" name="grupo" options={generalConfig.grupos} />
        <IntInput label={`Heartbeat (sugerencia: menor a ${generalConfig.global.max_tiempo_desconexion_temporada_falla_leve})`} name="heartbeat" />
        <IntInput label="Período de mediciones regulares" name="periodo_mediciones_regulares" />
        <hr />

        <MyHeading heading="Umbrales de lotes" />
        <MinMaxHead />
        <MinMaxInput label="Tiempo de procesamiento (s)" nameTemplate="%s_tiempo_procesamiento" />
        <MinMaxInput label="Tiempo de procesamiento de color (s)" nameTemplate="%s_tiempo_proc_color" />
        <MinMaxInput label="Firmeza (g/mm)" nameTemplate="%s_firmeza" />
        <MinMaxInput label="Calibre (mm)" nameTemplate="%s_calibre" />
        <MinMaxInput label="Peso (g)" nameTemplate="%s_peso" />
        <hr />

        <MyHeading heading="Alertas" />
        <FloatInput label="Alza de temperatura de caja (°C/min)" name="alerta_alza_temp_caja" />
        <FloatInput label="Alza de temperatura de sala (°C/min)" name="alerta_alza_temp_sala" />
        <MinMaxHead />
        <MinMaxInput label="Temperatura de caja (°C)" nameTemplate="alerta_%s_temp_caja" />
        <MinMaxInput label="Temperatura de sala (°C)" nameTemplate="alerta_%s_temp_sala" />
        <MinMaxInput label="Humedad de caja (%)" nameTemplate="alerta_%s_hum_caja" />
        <MinMaxInput label="Humedad de sala (%)" nameTemplate="alerta_%s_hum_sala" />

        <HStack my={4}>
          <Button flex={3} type="submit" shadow="lg">
            Actualizar
          </Button>
          <Alert flex={7} status={fpConfig.hay_cambios_sin_leer === 0 ? "success" : "warning"} borderRadius="lg">
            <AlertIcon />
            <AlertDescription>
              {fpConfig.hay_cambios_sin_leer === 0
              ? "Configuración cargada en el FirmPro."
              : "Hay cambios sin cargar en el FirmPro."}
            </AlertDescription>
          </Alert>
        </HStack>
      </VStack>
    </form>
  );
}
